import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { db, FirebaseCallable, functions, httpsCallable } from '@util/firebase';
import { getOrderById } from '@util/firestore/orders';
import {
  OrderDocument,
  OrderItemDocument,
  OrderStatesEnum,
} from 'models/order';
import { collection, doc, updateDoc } from '@firebase/firestore';

const colRef = collection(db, 'orders');
export const useOrder = (orderId?: string) => {
  const queryClient = useQueryClient();
  const data = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => getOrderById(orderId!),
    enabled: !!orderId,
  });

  const updateOrderItemState = useMutation({
    mutationFn: async ({
      item,
      state,
    }: {
      item: OrderItemDocument;
      state: OrderStatesEnum;
    }) => {
      const order = data.data;
      if (!order) throw new Error('Order not found');
      const sellers = order.sellers;
      const index =
        sellers?.[item.seller_id].findIndex((i) => i.id === item.id) ?? -1;
      if (index >= 0 && sellers) {
        sellers[item.seller_id][index].state = state;
        let allComplete = true;
        const states = new Set<number>();
        Object.values(sellers).forEach((items) =>
          items.forEach((item) => {
            states.add(item.state);
            if (item.state < 3) allComplete = false;
          })
        );

        let total_state = order.total_state;
        if (states.has(6) || states.has(7)) {
          total_state = 3;
        } else if (allComplete) {
          total_state = 2;
        } else if (states.has(2)) {
          total_state = 1;
        } else {
          total_state = 0;
        }

        await updateDoc(doc(colRef, order.id), {
          sellers,
          total_state,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['order', orderId],
      });
    },
  });

  const updateOrderByKey = useMutation({
    mutationFn: ({ key, value }: { key: keyof OrderDocument; value: any }) => {
      return updateDoc(doc(colRef, orderId), {
        [key]: value,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['order', orderId],
      });
    },
  });

  const fixLabels = useMutation({
    mutationFn: async () => {
      if (!orderId) throw new Error('No order id provided');
      const { data } = await httpsCallable<
        { orderId: string },
        { updated: number; errors: string[] }
      >(
        functions,
        FirebaseCallable.fixLabel
      )({ orderId });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['order', orderId],
      });
    },
  });
  return { data, updateOrderItemState, updateOrderByKey, fixLabels };
};
